import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { unitLabel, unitSubLabel } from '../utils/unitLabels';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaTruck, FaTrailer, FaUser, FaCalendarAlt,
  FaCheckCircle, FaTimes, FaUpload, FaSpinner,
  FaClock, FaMapMarkerAlt,
  FaEye, FaArrowLeft, FaTrash, FaEdit,
  FaPlus, FaSave, FaImage, FaFilePdf,
  FaPlay, FaStop, FaFlagCheckered, FaInfoCircle,
  FaWeight, FaRoad, FaArrowRight, FaCloudUploadAlt,
  FaTint, FaFilter, FaSearch, FaDownload, FaPrint,
  FaCheckDouble, FaThumbsUp, FaThumbsDown, FaUndo,
  FaExclamationTriangle, FaBuilding, FaUserTie,
  FaSync, FaEnvelope, FaChevronLeft, FaChevronRight,
  FaExternalLinkAlt
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import LoadPermitsPanel from '../components/LoadPermitsPanel';
import LoadTrackingPanel from '../components/LoadTrackingPanel';
import LoadDocumentsPanel from '../components/LoadDocumentsPanel';

// "L1 of 2 · Triaxle - 12m with Air Suspension" — which line item a unit is running. That is what tells
// unit 1 from unit 2 on a job with two units. The load payload already carries the job's line_items,
// so this is a lookup, not an extra request.
const unitLineItemLabel = (unit) => {
  const lines = Array.isArray(unit?.job?.line_items)
    ? unit.job.line_items
    : (Array.isArray(unit?.job?.items) ? unit.job.items : []);
  const li = lines.find((l) => l && l.id && l.id === unit.line_item_id) || null;
  const wording = li ? (li.title || li.description || li.commodity || null) : null;
  const tag = unit?.leg_label || (li?.line_number ? `Line ${li.line_number}` : null);
  if (tag && wording) return `${tag} · ${wording}`;
  return tag || wording || null;
};

function LoadsPage({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [loads, setLoads] = useState([]);
  const [filteredLoads, setFilteredLoads] = useState([]);
  const [user, setUser] = useState({});
  const [subbies, setSubbies] = useState([]);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, onConfirm }
  const [approvalData, setApprovalData] = useState({
    type: '',
    approve: false,
    notes: ''
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const [hasMore, setHasMore] = useState(true);

  const [filters, setFilters] = useState({
    dateRange: 'all',
    startDate: '',
    endDate: '',
    subbieId: '',
    jobNumber: '',
    status: '',
    showCompleted: false
  });

  // Helper function to get image URL
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    let filename = url;
    if (url.startsWith('/uploads/')) filename = url.replace('/uploads/', '');
    const encodedFilename = encodeURIComponent(filename);
    return window.getServerUrl(`/uploads/${encodedFilename}`);
  };

  // Handle "Request New Images"
  const handleRequestNewImages = async (load) => {
    try {
      const response = await fetch(`${API_URL}/notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: load.subcontractor_id,
          type: 'image_request',
          title: '📸 Request for Clearer Images',
          message: `Admin has requested clearer images for unit ${load.truck?.registration_number} on job ${load.job?.job_number}. Please upload new weighbridge and/or POD images.`,
          link: '/subbie-uploads'
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Request sent to subbie for new images');
        setShowApprovalModal(false);
        setShowViewModal(false);
      } else {
        toast.error(data.error || 'Failed to send request');
      }
    } catch (err) {
      console.error('Error sending image request:', err);
      toast.error('Failed to send request');
    }
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchSubbies();
    fetchLoads(1);
  }, []);

  const fetchSubbies = async () => {
    try {
      const subbieRes = await fetch(`${API_URL}/subcontractors`);
      const subbieData = await subbieRes.json();
      if (subbieData.success) setSubbies(subbieData.subcontractors || []);
    } catch (err) { console.error('Error fetching subbies:', err); }
  };

  const fetchLoads = async (page = 1, append = false) => {
    if (append) { setLoadingMore(true); } else { setLoading(true); }
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', pagination.limit);
      if (filters.subbieId) params.append('subcontractor_id', filters.subbieId);
      if (filters.jobNumber) params.append('job_number', filters.jobNumber);
      if (filters.status) params.append('status', filters.status);
      if (!filters.showCompleted) params.append('exclude_completed', 'true');
      if (filters.dateRange === 'today') params.append('date_range', 'today');
      else if (filters.dateRange === 'last7') params.append('date_range', 'last7');
      else if (filters.dateRange === 'custom') {
        if (filters.startDate) params.append('start_date', filters.startDate);
        if (filters.endDate) params.append('end_date', filters.endDate);
      }
      const response = await fetch(`${API_URL}/fleet/admin/loads?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        const newLoads = data.loads || [];
        if (append) {
          setLoads(prev => [...prev, ...newLoads]);
          setFilteredLoads(prev => [...prev, ...newLoads]);
        } else {
          setLoads(newLoads);
          setFilteredLoads(newLoads);
        }
        setPagination({ ...pagination, page, total: data.total || 0, totalPages: data.totalPages || 1 });
        setHasMore(page < data.totalPages);
      } else { setError(data.error || 'Failed to fetch loads'); }
    } catch (err) {
      console.error('Error fetching loads:', err);
      setError(err.message || 'Failed to load data');
    } finally { setLoading(false); setLoadingMore(false); }
  };

  const loadMore = () => { if (!loadingMore && hasMore) fetchLoads(pagination.page + 1, true); };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-gray-100 text-gray-700', 'on_route_to_loading': 'bg-indigo-100 text-indigo-700',
      'onsite': 'bg-blue-100 text-blue-700', 'loading': 'bg-yellow-100 text-yellow-700',
      'en_route': 'bg-purple-100 text-purple-700', 'at_destination': 'bg-orange-100 text-orange-700',
      'delivered': 'bg-yellow-100 text-yellow-700', 'completed': 'bg-green-100 text-green-700'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending', 'on_route_to_loading': 'Route to Load', 'onsite': 'On Site',
      'loading': 'Loading', 'en_route': 'En Route', 'at_destination': 'At Destination',
      'delivered': 'Delivered (Pending Approval)', 'completed': 'Completed'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': <FaClock className="text-gray-500" />, 'on_route_to_loading': <FaRoad className="text-indigo-500" />,
      'onsite': <FaMapMarkerAlt className="text-blue-500" />, 'loading': <FaTruck className="text-yellow-500" />,
      'en_route': <FaTruck className="text-purple-500" />, 'at_destination': <FaFlagCheckered className="text-orange-500" />,
      'delivered': <FaCheckCircle className="text-yellow-500" />, 'completed': <FaCheckCircle className="text-green-500" />
    };
    return icons[status] || <FaClock className="text-gray-500" />;
  };

  const statusOrder = ['pending', 'on_route_to_loading', 'onsite', 'loading', 'en_route', 'at_destination', 'delivered', 'completed'];
  const getStatusIndex = (status) => statusOrder.indexOf(status) + 1;

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchLoads(1, false);
  };

  const clearFilters = () => {
    const newFilters = { dateRange: 'all', startDate: '', endDate: '', subbieId: '', jobNumber: '', status: '', showCompleted: false };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchLoads(1, false);
  };

  const handleUpdateStatus = async (unitId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/fleet/fleet-allocations/${unitId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus })
      });
      const data = await response.json();
      if (data.success) { toast.success(`Status updated to ${getStatusLabel(newStatus)}`); fetchLoads(pagination.page, false); }
      else { toast.error(data.error || 'Failed to update status'); }
    } catch (err) { console.error('Error updating status:', err); toast.error('Failed to update status'); }
  };

  const handleApproval = async (unitId, type, approved, notes = '') => {
    try {
      const response = await fetch(`${API_URL}/fleet/fleet-allocations/${unitId}/pod/approve`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ approved, notes, type })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`${type} ${approved ? 'approved' : 'rejected'} successfully`);
        setShowApprovalModal(false); setSelectedLoad(null); fetchLoads(pagination.page, false);
      } else { toast.error(data.error || 'Failed to approve'); }
    } catch (err) { console.error('Error approving:', err); toast.error('Failed to approve'); }
  };

  const handleViewDetails = (load) => { setSelectedLoad(load); setShowViewModal(true); };
  const handleOpenApproval = (load, type) => { setSelectedLoad(load); setApprovalData({ type, approve: false, notes: '' }); setShowApprovalModal(true); };

  const calculateTruckSweat = (unit) => {
    if (unit.weighbridge_weight_mt && unit.pod_weight_mt) return unit.weighbridge_weight_mt - unit.pod_weight_mt;
    return null;
  };

  const groupLoadsByJob = () => {
    const grouped = {};
    filteredLoads.forEach(load => {
      const jobId = load.job_id;
      if (!grouped[jobId]) {
        grouped[jobId] = { job: load.job || { job_number: 'Unknown Job' }, subbie: load.subcontractor || { company_name: 'Unknown Subbie' }, units: [] };
      }
      grouped[jobId].units.push(load);
    });
    return grouped;
  };

  const groupedLoads = groupLoadsByJob();

  if (loading) {
    return (
      <AdminLayout activeTab="loads" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center"><FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" /><p className="text-gray-500">Loading loads...</p></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout activeTab="loads" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Loads</h2>
          <p className="text-gray-500">{error}</p>
          <button onClick={() => fetchLoads(1, false)} className="mt-4 px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#00A86B' }}>Retry</button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="loads" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">🚛 All Loads</h1>
          <p className="text-sm text-gray-500">{filteredLoads.length} loads • Page {pagination.page} of {pagination.totalPages}</p>
        </div>
        <button onClick={() => fetchLoads(1, false)} className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors">
          <FaSync size={14} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4 mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2"><FaFilter className="text-gray-400" /><span className="text-sm font-medium text-gray-600">Filters:</span></div>
          <select value={filters.dateRange} onChange={(e) => handleFilterChange('dateRange', e.target.value)} className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green" style={{ borderColor: '#E8ECEF' }}>
            <option value="all">All Dates</option><option value="today">Today</option><option value="last7">Last 7 Days</option><option value="custom">Custom Range</option>
          </select>
          {filters.dateRange === 'custom' && (<><input type="date" value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8ECEF' }} /><span className="text-gray-400">to</span><input type="date" value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8ECEF' }} /></>)}
          <select value={filters.subbieId} onChange={(e) => handleFilterChange('subbieId', e.target.value)} className="px-3 py-2 border rounded-lg text-sm min-w-[150px]" style={{ borderColor: '#E8ECEF' }}>
            <option value="">All Subbies</option>
            {subbies.map(s => (<option key={s.id} value={s.id}>{s.company_name}</option>))}
          </select>
          <input type="text" value={filters.jobNumber} onChange={(e) => handleFilterChange('jobNumber', e.target.value)} placeholder="Search Job #" className="px-3 py-2 border rounded-lg text-sm w-40" style={{ borderColor: '#E8ECEF' }} />
          <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8ECEF' }}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option><option value="on_route_to_loading">Route to Load</option><option value="onsite">On Site</option>
            <option value="loading">Loading</option><option value="en_route">En Route</option><option value="at_destination">At Destination</option>
            <option value="delivered">Delivered</option><option value="completed">Completed</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={filters.showCompleted} onChange={(e) => handleFilterChange('showCompleted', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500" />Show Completed</label>
          <button onClick={clearFilters} className="text-sm text-red-500 hover:text-red-700">Clear All</button>
        </div>
      </div>

      {/* Loads List */}
      {filteredLoads.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">📦</div><h3 className="text-xl font-semibold text-navy">No Loads Found</h3>
          <p className="text-sm text-gray-500">No loads match your current filters.</p>
        </div>
      ) : (
        <>
          <div className="space-y-6">
            {Object.entries(groupedLoads).map(([jobId, group]) => {
              const jobType = group.job?.quote_type || 'minerals';
              const jobTypeColor = { minerals: '#f59e0b', agri: '#22c55e', abnormal: '#8b5cf6', container: '#eab308', general: '#3b82f6' }[jobType] || '#64748b';
              const jobTypeLabel = { minerals: 'Minerals', agri: 'Agri', abnormal: 'Abnormal', container: 'Container', general: 'General' }[jobType] || jobType;

              return (
                <div key={jobId} className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF', borderLeft: `4px solid ${jobTypeColor}` }}>
                  {/* ═══ JOB-GROUP HEADER — COMPACT ═══ */}
                  <div className="flex flex-wrap justify-between items-start mb-3 pb-3 border-b" style={{ borderColor: '#E8ECEF' }}>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-bold text-navy">{group.job.job_number || 'Unknown Job'}</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: `${jobTypeColor}15`, color: jobTypeColor }}>{jobTypeLabel}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{group.units.length} units</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1"><FaUserTie className="text-green-500" />{group.subbie?.company_name || 'Unknown Subbie'}</span>
                        {group.job.cross_border && (<span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">🛂 Cross-Border</span>)}
                      </div>
                    </div>
                  </div>

                  {/* ═══ UNITS ═══ */}
                  <div className="space-y-3">
                    {group.units.map((unit, index) => {
                      const truckSweat = calculateTruckSweat(unit);
                      const isDelivered = unit.status === 'delivered';
                      const isCompleted = unit.status === 'completed';
                      const isPendingApproval = isDelivered && unit.pod_status === 'pending';
                      const unitJobType = unit.job?.quote_type || 'minerals';
                      const typeBar = { minerals: 'border-l-red-400', agri: 'border-l-green-400', abnormal: 'border-l-purple-400', container: 'border-l-yellow-400', general: 'border-l-blue-400' }[unitJobType] || 'border-l-gray-200';
                      const unitIsMinerals = unitJobType === 'minerals' || unitJobType === 'agri';

                      return (
                        <div key={unit.id} className={`bg-white rounded-lg p-3 border border-l-4 ${typeBar} ${isCompleted ? 'opacity-70' : ''}`} style={{ borderColor: '#E8ECEF' }}>
                          <div className="flex flex-wrap items-center gap-4 mb-3">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(unit.status)}
                              <span className={getStatusBadge(unit.status)}>{getStatusLabel(unit.status)}</span>
                            </div>
                            <span className="text-sm font-medium text-navy">Unit {index + 1}</span>
                            {/* Which line item this unit is on, so unit 1 and unit 2 are never confused. */}
                            {unitLineItemLabel(unit) && (
                              <span className="text-xs font-medium" style={{ color: '#0A1929' }}>{unitLineItemLabel(unit)}</span>
                            )}
                            <span className="text-xs text-gray-500">Arrival: {unit.arrival_date || 'N/A'}</span>
                            {unitIsMinerals && unit.weighbridge_weight_mt && (<span className="text-xs text-green-600 font-medium">⚖️ {unit.weighbridge_weight_mt} MT</span>)}
                            {unitIsMinerals && unit.pod_weight_mt && (<span className="text-xs text-blue-600 font-medium">📄 {unit.pod_weight_mt} MT</span>)}
                            {unitIsMinerals && truckSweat !== null && truckSweat > 0 && (<span className="text-xs text-orange-600 font-medium flex items-center gap-1"><FaTint size={10} /> {truckSweat.toFixed(2)} MT Sweat</span>)}
                            {isPendingApproval && (<span className="text-xs text-yellow-600 flex items-center gap-1"><FaClock size={10} /> Needs Approval</span>)}
                            {isCompleted && (<span className="text-xs text-green-600 flex items-center gap-1"><FaCheckCircle size={10} /> Complete</span>)}
                            {unit.tracking_link && (
                              <>
                                <span className="text-xs text-blue-600 flex items-center gap-1"><FaExternalLinkAlt size={10} /> Tracking active</span>
                                {/* The same red pulsing pill the client sees, so "this load has a live
                                    tracking link" reads identically on both sides. */}
                                <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-600 rounded-full animate-pulse flex items-center gap-1">
                                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                  LIVE
                                </span>
                              </>
                            )}
                          </div>

                          {/* Progress / route bar */}
                          <div className="mb-3">
                            <div className="flex justify-between text-xs mb-1" style={{ color: '#4A5568' }}>
                              <span>Progress</span>
                              <span>{Math.round((getStatusIndex(unit.status) / 8) * 100)}%</span>
                            </div>
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${Math.round((getStatusIndex(unit.status) / 8) * 100)}%`, backgroundColor: isCompleted ? '#16a34a' : '#00A86B' }}
                              />
                            </div>
                            <div className="flex justify-between text-xs mt-1" style={{ color: '#4A5568' }}>
                              <span>Pending</span><span>Route</span><span>Site</span><span>Loading</span><span>En Route</span><span>Dest</span><span>Delivered</span><span>Done</span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <div>
                              <p className="text-xs text-gray-500">Tractor</p>
                              <p className="font-medium text-sm text-navy">{unitLabel(unit.truck) || 'N/A'}</p>
                              <p className="text-xs text-gray-400">{unitSubLabel(unit.truck)}</p>
                            </div>
                            {unit.trailer_1?.registration_number && (
                              <div>
                                <p className="text-xs text-gray-500">Trailer 1</p>
                                <p className="font-medium text-sm text-navy">{unit.trailer_1.registration_number}</p>
                                <p className="text-xs text-gray-400">{unit.trailer_1.trailer_type}</p>
                              </div>
                            )}
                            {unit.trailer_2?.registration_number && (
                              <div>
                                <p className="text-xs text-gray-500">Trailer 2</p>
                                <p className="font-medium text-sm text-navy">{unit.trailer_2.registration_number}</p>
                                <p className="text-xs text-gray-400">{unit.trailer_2.trailer_type}</p>
                              </div>
                            )}
                            <div>
                              <p className="text-xs text-gray-500">Driver</p>
                              <p className="font-medium text-sm text-navy">{unit.driver?.full_name || 'N/A'}</p>
                              <p className="text-xs text-gray-400">{unit.driver?.phone}</p>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => handleViewDetails(unit)} style={{ backgroundColor: '#2563eb', color: '#fff', fontWeight: '600', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1d4ed8'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2563eb'}>
                              <FaEye size={12} /> View Details
                            </button>
                            {unit.tracking_link && (
                              <a
                                href={unit.tracking_link.startsWith('http') ? unit.tracking_link : `https://${unit.tracking_link}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ backgroundColor: '#0891b2', color: '#fff', fontWeight: '600', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                              >
                                <FaExternalLinkAlt size={12} /> Track
                              </a>
                            )}
                            {isPendingApproval && (
                              <button onClick={() => handleOpenApproval(unit, 'pod')} style={{ backgroundColor: '#059669', color: '#fff', fontWeight: '600', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#047857'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#059669'}>
                                <FaCheckDouble size={12} /> Review & Approve
                              </button>
                            )}
                            {!isCompleted && !isDelivered && (
                              <select value="" onChange={(e) => { if (e.target.value) handleUpdateStatus(unit.id, e.target.value); }} className="px-2 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-navy bg-white focus:outline-none focus:border-navy">
                                <option value="">Change Status ▾</option>
                                <option value="on_route_to_loading">On Route to Load</option>
                                <option value="onsite">On Site</option>
                                <option value="loading">Loading</option>
                                <option value="en_route">En Route</option>
                                <option value="at_destination">At Destination</option>
                                <option value="delivered">Delivered</option>
                              </select>
                            )}
                            <button
                              onClick={() => {
                                setConfirmModal({
                                  title: 'Remove Allocation',
                                  message: 'Are you sure you want to remove this allocation? This will permanently delete it.',
                                  onConfirm: async () => {
                                    try {
                                      const response = await fetch(`${API_URL}/fleet/fleet-allocations/${unit.id}`, { method: 'DELETE' });
                                      const data = await response.json();
                                      if (data.success) { toast.success('Unit removed successfully'); fetchLoads(pagination.page, false); }
                                      else { toast.error(data.error || 'Failed to remove'); }
                                    } catch (err) { console.error('Error removing unit:', err); toast.error('Failed to remove unit'); }
                                  }
                                });
                              }}
                              style={{ backgroundColor: '#dc2626', color: '#fff', fontWeight: '600', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#b91c1c'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#dc2626'}
                            >
                              <FaTrash size={12} /> Remove
                            </button>
                            {unit.weighbridge_image_url && (<span className="text-xs text-green-600 flex items-center gap-1">✅ Weighbridge</span>)}
                            {unit.pod_image_url && (<span className="text-xs text-green-600 flex items-center gap-1">✅ POD</span>)}
                            {unit.pod_status === 'rejected' && (<span className="text-xs text-red-600 flex items-center gap-1">❌ POD Rejected</span>)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="text-center mt-6">
              <button onClick={loadMore} disabled={loadingMore} className="px-6 py-3 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors disabled:opacity-50 flex items-center gap-2 mx-auto">
                {loadingMore ? <FaSpinner className="animate-spin" /> : <FaChevronRight size={14} />}
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
              <p className="text-xs text-gray-400 mt-2">Showing {filteredLoads.length} of {pagination.total} loads</p>
            </div>
          )}
          <div className="flex justify-between items-center mt-4 text-xs text-gray-400">
            <span>Page {pagination.page} of {pagination.totalPages}</span><span>{pagination.total} total loads</span>
          </div>
        </>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowApprovalModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-4">📄 Review {approvalData.type === 'pod' ? 'POD' : 'Weighbridge'} Approval</h2>
            <div className="mb-6">
              <p className="text-sm text-gray-500">Unit: {selectedLoad.truck?.registration_number}</p>
              <p className="text-sm text-gray-500">Job: {selectedLoad.job?.job_number || 'N/A'}</p>
              <p className="text-sm text-gray-500">Subbie: {selectedLoad.subcontractor?.company_name || 'N/A'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="border rounded-lg p-4" style={{ borderColor: '#E8ECEF' }}>
                <p className="text-sm font-medium text-navy mb-2">Weighbridge Ticket</p>
                {selectedLoad.weighbridge_image_url ? (<><img src={getImageUrl(selectedLoad.weighbridge_image_url)} alt="Weighbridge" className="w-full rounded-lg border max-h-48 object-contain cursor-pointer hover:opacity-80" style={{ borderColor: '#E8ECEF' }} onClick={() => window.open(getImageUrl(selectedLoad.weighbridge_image_url), '_blank')} onError={e => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f3f4f6"/%3E%3Ctext x="100" y="100" font-family="Arial" font-size="14" fill="%239ca3af" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E'; }} /><div className="mt-2 text-xs"><p className="text-gray-500">Weight: {selectedLoad.weighbridge_weight_mt || 'N/A'} MT</p><a href={getImageUrl(selectedLoad.weighbridge_image_url)} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 underline text-xs">View Full Image ↗</a></div></>) : (<p className="text-sm text-gray-400">No image uploaded</p>)}
              </div>
              <div className="border rounded-lg p-4" style={{ borderColor: '#E8ECEF' }}>
                <p className="text-sm font-medium text-navy mb-2">POD</p>
                {selectedLoad.pod_image_url ? (<><img src={getImageUrl(selectedLoad.pod_image_url)} alt="POD" className="w-full rounded-lg border max-h-48 object-contain cursor-pointer hover:opacity-80" style={{ borderColor: '#E8ECEF' }} onClick={() => window.open(getImageUrl(selectedLoad.pod_image_url), '_blank')} onError={e => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f3f4f6"/%3E%3Ctext x="100" y="100" font-family="Arial" font-size="14" fill="%239ca3af" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E'; }} /><div className="mt-2 text-xs"><p className="text-gray-500">Weight: {selectedLoad.pod_weight_mt || 'N/A'} MT</p><a href={getImageUrl(selectedLoad.pod_image_url)} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 underline text-xs">View Full Image ↗</a></div></>) : (<p className="text-sm text-gray-400">No image uploaded</p>)}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm font-medium text-navy mb-2">Weight Comparison</p>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><p className="text-gray-500">Weighbridge</p><p className="font-bold text-green-600">{selectedLoad.weighbridge_weight_mt || 'N/A'} MT</p></div>
                <div><p className="text-gray-500">POD</p><p className="font-bold text-blue-600">{selectedLoad.pod_weight_mt || 'N/A'} MT</p></div>
                <div><p className="text-gray-500">Difference</p><p className="font-bold text-orange-600">{selectedLoad.weighbridge_weight_mt && selectedLoad.pod_weight_mt ? (selectedLoad.weighbridge_weight_mt - selectedLoad.pod_weight_mt).toFixed(2) : 'N/A'} MT</p></div>
              </div>
            </div>
            <div className="mb-6">
              <button onClick={() => handleRequestNewImages(selectedLoad)} style={{ backgroundColor: '#eab308', color: '#fff', fontWeight: '600', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#ca8a04'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#eab308'}>
                <FaEnvelope size={14} /> Request New Images
              </button>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Approval Notes</label>
              <textarea value={approvalData.notes} onChange={(e) => setApprovalData({ ...approvalData, notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows="2" placeholder="Add any notes..." />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button onClick={() => setShowApprovalModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleApproval(selectedLoad.id, approvalData.type, false, approvalData.notes)} style={{ backgroundColor: '#dc2626', color: '#fff', fontWeight: '600', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', border: 'none', cursor: 'pointer' }}><FaThumbsDown size={14} /> Reject</button>
              <button onClick={() => handleApproval(selectedLoad.id, approvalData.type, true, approvalData.notes)} style={{ backgroundColor: '#059669', color: '#fff', fontWeight: '600', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', border: 'none', cursor: 'pointer' }}><FaThumbsUp size={14} /> Approve</button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && selectedLoad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowViewModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy">📋 Load Details</h2>
              <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-navy"><FaTimes size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div><p className="text-xs text-gray-500">Job Number</p><p className="font-medium text-navy">{selectedLoad.job?.job_number || 'N/A'}</p></div>
              <div><p className="text-xs text-gray-500">Subcontractor</p><p className="font-medium text-navy">{selectedLoad.subcontractor?.company_name || 'N/A'}</p></div>
              <div><p className="text-xs text-gray-500">Tractor</p><p className="font-medium text-navy">{selectedLoad.truck?.registration_number || 'N/A'}</p></div>
              <div><p className="text-xs text-gray-500">Driver</p><p className="font-medium text-navy">{selectedLoad.driver?.full_name || 'N/A'}</p></div>
              <div><p className="text-xs text-gray-500">Status</p><span className={getStatusBadge(selectedLoad.status)}>{getStatusLabel(selectedLoad.status)}</span></div>
              <div><p className="text-xs text-gray-500">Arrival Date</p><p className="font-medium text-navy">{selectedLoad.arrival_date || 'N/A'}</p></div>
              {selectedLoad.weighbridge_weight_mt && (<div><p className="text-xs text-gray-500">Weighbridge Weight</p><p className="font-bold text-green-600">{selectedLoad.weighbridge_weight_mt} MT</p></div>)}
              {selectedLoad.pod_weight_mt && (<div><p className="text-xs text-gray-500">POD Weight</p><p className="font-bold text-blue-600">{selectedLoad.pod_weight_mt} MT</p></div>)}
            </div>

            {/* Tracking link + login for this load. Admin and subbie can add or replace it; the
                client only ever sees it. Expiry is shown because provider share links lapse. */}
            <div className="mb-4">
              <LoadTrackingPanel
                load={selectedLoad}
                saveUrl={`${API_URL}/fleet-allocations/${selectedLoad.id}/telematics`}
                canEdit
                onSaved={(updated) => {
                  if (updated) setSelectedLoad((prev) => ({ ...prev, ...updated }));
                  // The modal opens from a COPY of the row (the `loads` list), so that copy has to be
                  // refreshed too. Without this the link looked saved until you closed the modal and
                  // reopened it, when the stale row came back and the link appeared to vanish.
                  fetchLoads(pagination.page, false);
                }}
              />
            </div>

            {/* Anything else that belongs to this load: border permits, proceed-to-border letters,
                customs releases, escort letters. Office, subbie and client all see the same pile. */}
            <div className="mb-4">
              <LoadDocumentsPanel loadId={selectedLoad.id} canDelete />
            </div>

            {selectedLoad.comments && (<div className="mb-4"><p className="text-xs text-gray-500">Comments</p><p className="text-sm text-gray-700">{selectedLoad.comments}</p></div>)}
                        {selectedLoad.job?.job_description && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-1">Job Description</p>
                <p className="text-sm text-gray-700 whitespace-pre-line">{selectedLoad.job.job_description}</p>
              </div>
            )}
            {(() => {
              const li = (Array.isArray(selectedLoad.job?.line_items) && selectedLoad.job.line_items.length)
                ? selectedLoad.job.line_items
                : (Array.isArray(selectedLoad.job?.items) && selectedLoad.job.items.length ? selectedLoad.job.items : []);
              return li.length > 0 ? (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-1">Line Items</p>
                  <div className="space-y-2">
                    {li.map((x, i) => (
                      <div key={x.id || i} className="border rounded-lg p-2.5" style={{ borderColor: '#E8ECEF' }}>
                        <div className="flex justify-between gap-2">
                          <p className="text-sm font-medium text-navy">{x.title || x.description || x.commodity || 'Line item'}</p>
                          <div className="text-right flex-shrink-0">
                            {(x.cost_price || x.rate || 0) > 0 && <p className="text-xs text-gray-600">Cost: <span className="font-semibold">R{Number(x.cost_price || x.rate || 0).toFixed(2)}</span></p>}
                            {(x.client_price || x.unit_price || 0) > 0 && <p className="text-xs text-gray-600">Client: <span className="font-semibold text-navy">R{Number(x.client_price || x.unit_price || 0).toFixed(2)}</span></p>}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {x.quantity || 1} x {x.unit || 'load'}
                          {(x.pickup_location || x.delivery_location) ? ` · ${x.pickup_location || '?'} → ${x.delivery_location || '?'}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}
<LoadPermitsPanel loadId={selectedLoad.id} />
            <div className="flex justify-end pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button onClick={() => setShowViewModal(false)} className="px-4 py-2 bg-navy text-white rounded-lg text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CONFIRM MODAL ═══ */}
      {confirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }} onClick={() => setConfirmModal(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,.2)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#0A1929' }}>{confirmModal.title}</h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: '#475569', lineHeight: 1.5 }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmModal(null)} style={{ padding: '10px 22px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button onClick={() => { const fn = confirmModal.onConfirm; setConfirmModal(null); fn(); }} style={{ padding: '10px 22px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default LoadsPage;